import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, CAP_LABELS, EQUIPMENT_LIST, fmtErr } from "@/lib/api";

const CAPS = ["push", "pull", "squat", "bend_lift", "rotation", "endurance"];
const BLOCKS = ["prepare", "learn", "train", "apply", "control", "recover"];
const EMPTY = { name: "", capability: "push", blocks: ["train"], equipment: ["bodyweight"],
  difficulty: 2, regression: "", progression: "", prescription: "", purpose: "", tags: [], body_parts: [] };

export default function LibraryTab() {
  const [movements, setMovements] = useState([]);
  const [filter, setFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/coach/movements")
    .then((r) => setMovements(Array.isArray(r.data) ? r.data : []))
    .catch((e) => { toast.error(fmtErr(e)); setMovements([]); });
  useEffect(() => { load(); }, []);

  const toggle = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coach/movements", form);
      toast.success("Movement added to the library");
      setShowAdd(false); setForm(EMPTY); load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const shown = filter ? movements.filter((m) => m.capability === filter) : movements;

  return (
    <div data-testid="coach-library">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-display uppercase text-4xl">Movement library <span className="text-zinc-600">({movements.length})</span></h2>
        <button data-testid="library-add-open" onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-2 bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold px-5 py-3 hover:bg-white transition-colors">
          <Plus size={14} /> Add movement
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("")} data-testid="library-filter-all"
          className={`border px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold transition-colors ${!filter ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-400 hover:border-volt"}`}>All</button>
        {CAPS.map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`library-filter-${c}`}
            className={`border px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold transition-colors ${filter === c ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-400 hover:border-volt"}`}>
            {CAP_LABELS[c]}
          </button>
        ))}
      </div>

      {showAdd && (
        <form onSubmit={create} data-testid="library-add-form" className="border border-volt/40 bg-zinc-950 p-6 mb-6 grid sm:grid-cols-2 gap-4">
          <input required placeholder="Movement name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-name" />
          <select value={form.capability} onChange={(e) => setForm({ ...form, capability: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-capability">
            {CAPS.map((c) => <option key={c} value={c}>{CAP_LABELS[c]}</option>)}
          </select>
          <input placeholder="Prescription (e.g. 3 × 10)" value={form.prescription} onChange={(e) => setForm({ ...form, prescription: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-prescription" />
          <input placeholder="Purpose (why we train it)" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-purpose" />
          <input placeholder="Regression (easier) *" required value={form.regression} onChange={(e) => setForm({ ...form, regression: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-regression" />
          <input placeholder="Progression (harder) *" required value={form.progression} onChange={(e) => setForm({ ...form, progression: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" data-testid="lib-progression" />
          <div className="sm:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Blocks · Difficulty {form.difficulty}</p>
            <div className="flex flex-wrap gap-2 items-center">
              {BLOCKS.map((b) => (
                <button type="button" key={b} onClick={() => toggle("blocks", b)} data-testid={`lib-block-${b}`}
                  className={`border px-2.5 py-1.5 text-[10px] uppercase font-bold transition-colors ${form.blocks.includes(b) ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-500"}`}>{b}</button>
              ))}
              <input type="range" min="1" max="5" value={form.difficulty} data-testid="lib-difficulty"
                onChange={(e) => setForm({ ...form, difficulty: Number(e.target.value) })} className="accent-[#ff5c1a]" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Equipment</p>
            <div className="flex flex-wrap gap-2">
              {EQUIPMENT_LIST.map((e2) => (
                <button type="button" key={e2.id} onClick={() => toggle("equipment", e2.id)} data-testid={`lib-equip-${e2.id}`}
                  className={`border px-2.5 py-1.5 text-[10px] uppercase font-bold transition-colors ${form.equipment.includes(e2.id) ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-500"}`}>{e2.label}</button>
              ))}
            </div>
          </div>
          <button className="sm:col-span-2 bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold py-4 hover:bg-white transition-colors" data-testid="lib-submit">
            Save movement
          </button>
        </form>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {shown.map((m) => (
          <div key={m.id} data-testid={`movement-card-${m.id}`} className="border border-zinc-800 bg-zinc-950 p-5 group">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-bold text-zinc-100">{m.name}</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-volt font-bold mt-1">
                  {CAP_LABELS[m.capability]} · L{m.difficulty} · {(m.blocks || []).join("/")}
                </p>
              </div>
              <button onClick={async () => { await api.delete(`/coach/movements/${m.id}`); load(); }}
                data-testid={`movement-delete-${m.id}`} aria-label="Delete movement"
                className="text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                <Trash2 size={15} />
              </button>
            </div>
            {m.purpose && <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{m.purpose}</p>}
            <p className="text-[10px] uppercase tracking-[0.08em] text-zinc-600 mt-3">
              R: {m.regression || "—"} · P: {m.progression || "—"}
            </p>
            <p className="text-[10px] text-zinc-600 mt-1">{(m.equipment || []).join(", ")}</p>
          </div>
        ))}
        {!shown.length && <p data-testid="library-empty" className="border border-zinc-800 bg-zinc-950 p-6 text-sm text-zinc-600">No movements in the library yet.</p>}
      </div>
    </div>
  );
}
